// frontend/src/config/menuSections.js
//
// Fuente única del árbol de navegación (secciones, ítems y su filtrado por permiso).
// Extraído de components/NavegacionLateral.js para que la navegación de escritorio (riel +
// flyout) y la navegación mobile (bottom nav + drawer "Más") lean exactamente el mismo dato y
// la misma lógica de permisos, sin duplicar el array a mano ni casos especiales como el de ART.

// Cada sección/ítem declara el/los permiso(s) que la habilitan (ver auth/roles.js).
// Una sección con `permisos` (array) se muestra si el usuario tiene AL MENOS UNO —
// permite que, por ejemplo, SUPERVISOR vea la sección ADMINISTRACIÓN solo por el
// ítem Datos Maestros, y RECEPCION vea CONFIGURACIONES solo por el ítem Turnero.
export const menuSections = [
    {
        id: 'clientes',
        title: 'CLIENTES',
        icon: 'bi bi-people-fill',
        permiso: 'clientes.ver',
        items: [
            { to: '/clientes', icon: 'bi bi-people-fill', label: 'Clientes' }
        ]
    },
    {
        id: 'tramites',
        title: 'TRÁMITES',
        icon: 'bi bi-journal-richtext',
        permiso: 'tramites.ver',
        items: [
            { to: '/tramites', icon: 'bi bi-journal-richtext', label: 'Trámites' }
        ]
    },
    {
        id: 'art',
        title: 'ART',
        icon: 'bi bi-heart-pulse-fill',
        permiso: 'art.ver',
        items: [
            { to: '/art', icon: 'bi bi-house-door-fill', label: 'Inicio ART' },
            { to: '/art/tramites', icon: 'bi bi-folder-fill', label: 'Trámites ART' }
        ]
    },
    {
        id: 'rti',
        title: 'RTI',
        icon: 'bi bi-folder-fill',
        permiso: 'rti.ver',
        items: [
            { to: '/rti', icon: 'bi bi-house-door-fill', label: 'Inicio RTI' },
            { to: '/rti/tramites', icon: 'bi bi-file-earmark-medical-fill', label: 'Trámites RTI' }
        ]
    },
    {
        id: 'recepcion',
        title: 'TURNOS',
        icon: 'bi bi-calendar-check-fill',
        permiso: 'recepcion.ver',
        items: [
            { to: '/recepcion', icon: 'bi bi-speedometer2', label: 'Panel Diario' },
            { to: '/recepcion/turnero', icon: 'bi bi-calendar3', label: 'Turnero' },
            { to: '/recepcion/atencion', icon: 'bi bi-person-check-fill', label: 'Atención' },
            { to: '/recepcion/historico', icon: 'bi bi-clock-history', label: 'Histórico de Turnos' },
            // Los turnos ANSES tienen agenda propia: incluyen los que van con poder de un
            // abogado del estudio, que no ocupan lugar en el turnero.
            { to: '/anses/turnos', icon: 'bi bi-building', label: 'Turnos ANSES' }
        ]
    },
    {
        id: 'general',
        title: 'GENERAL',
        icon: 'bi bi-grid-1x2-fill',
        // RECEPCION no tiene 'general.ver' (pierde Médicos/Gastos) pero sí 'agenda.ver'.
        permisos: ['general.ver', 'agenda.ver'],
        items: [
            { key: 'medicos', to: '/medicos', icon: 'bi bi-person-badge-fill', label: 'Médicos', permiso: 'general.ver' },
            { key: 'gastos', to: '/gastos', icon: 'bi bi-currency-dollar', label: 'Gastos', permiso: 'general.ver' },
            { key: 'agenda', to: '/calendario', icon: 'bi bi-calendar-event-fill', label: 'Agenda', permiso: 'agenda.ver' }
        ]
    },
    {
        id: 'listados',
        title: 'LISTADOS',
        icon: 'bi bi-list-check',
        permiso: 'listados.ver',
        items: [
            // Los 5 tramos del ciclo del trámite, en su orden: a liquidar -> para aprobar ->
            // favorables -> altas -> deudores. Mismo orden que la barra de accesos directos (ver
            // LISTADOS_CICLO en pages/Listados/definiciones.js). Los listados auxiliares van en
            // "OTROS LISTADOS", abajo.
            { to: '/listados/a-liquidar', icon: 'bi bi-hourglass-split', label: 'A liquidar' },
            { to: '/listados/para-aprobar', icon: 'bi bi-hourglass-bottom', label: 'Para aprobar' },
            { to: '/listados/a-cobrar', icon: 'bi bi-cash-stack', label: 'Favorables' },
            { to: '/listados/altas', icon: 'bi bi-check2-circle', label: 'Altas' },
            { to: '/listados/deudores', icon: 'bi bi-exclamation-triangle', label: 'Deudores' },
        ]
    },
    {
        // Listados de trabajo que no son un tramo del ciclo (por ahora, Jauregui de reajuste). Acá
        // van los que se vayan sumando.
        id: 'listadosOtros',
        title: 'OTROS LISTADOS',
        icon: 'bi bi-collection',
        permiso: 'listados.ver',
        items: [
            { to: '/listados/cargar-jauregui', icon: 'bi bi-calculator', label: 'Para cargar Jauregui' },
            { to: '/listados/volver-a-contactar', icon: 'bi bi-telephone-outbound', label: 'Volver a contactar' },
            { to: '/listados/archivo', icon: 'bi bi-archive', label: 'Archivo' },
            { to: '/listados/carpetas-eliminadas', icon: 'bi bi-folder-x', label: 'Carpetas eliminadas' },
        ]
    },
    {
        id: 'reportes',
        title: 'REPORTES',
        icon: 'bi bi-bar-chart-fill',
        // RECEPCION pierde la sección completa, incluidos los reportes de recepción.
        permiso: 'reportes.ver',
        items: [
            { to: '/reportes/gastos', icon: 'bi bi-graph-up', label: 'Reportes de gastos' },
            { to: '/reportes/tramites', icon: 'bi bi-file-earmark-bar-graph-fill', label: 'Reportes de trámites' },
            { to: '/reportes/referencias', icon: 'bi bi-diagram-3-fill', label: 'Reportes de referencias' },
            { to: '/reportes/recepcion', icon: 'bi bi-inbox-fill', label: 'Reportes de recepción' },
            { to: '/anses/reportes', icon: 'bi bi-building', label: 'Reportes de turnos ANSES' }
        ]
    },
    {
        id: 'herramientas',
        title: 'HERRAMIENTAS',
        icon: 'bi bi-tools',
        permiso: 'herramientas.ver',
        items: [
            { to: '/correos', icon: 'bi bi-envelope-fill', label: 'Correos' },
            { to: '/herramientas/documentos', icon: 'bi bi-file-earmark-richtext', label: 'Generar Documentos' },
            { to: '/herramientas/historial-laboral', icon: 'bi bi-file-earmark-text', label: 'Historia Laboral ANSES' },
            // Feature en prueba: ADMIN, PROGRAMADOR y SUPERVISOR.
            // Página de control del robot de ANSES (oct-2026); el seguimiento por trámite está en Para aprobar.
            { to: '/herramientas/seguimiento-expedientes', icon: 'bi bi-cpu', label: 'Control del robot ANSES', permiso: 'herramientas.expedientes' },
            { to: '/herramientas/feedback', icon: 'bi bi-chat-square-text', label: 'Feedback' },
            // Resultados de la encuesta de satisfacción: sólo PROGRAMADOR. El permiso es exclusivo,
            // así que el '*' de ADMIN no lo cubre y el ítem no le aparece (ver auth/roles.js).
            { to: '/encuesta/resultados', icon: 'bi bi-clipboard2-check', label: 'Encuesta de satisfacción', permiso: 'encuesta.resultados' }
        ]
    },
    {
        id: 'administracion',
        title: 'ADMINISTRACIÓN',
        icon: 'bi bi-shield-lock-fill',
        // SUPERVISOR entra solo por Datos Maestros; RECEPCION entra solo por Datos Maestros >
        // Carpetas (datosMaestros.carpetas.ver); el resto de los ítems queda ADMIN+PROGRAMADOR.
        permisos: ['administracion.ver', 'datosMaestros.ver', 'datosMaestros.carpetas.ver', 'correoExterno.ver'],
        items: [
            { to: '/admin/usuarios', icon: 'bi bi-person-gear', label: 'Usuarios', permiso: 'administracion.ver' },
            { to: '/admin/logs', icon: 'bi bi-journal-text', label: 'Logs', permiso: 'administracion.ver' },
            // Array: 'datosMaestros.ver' da acceso a todas las pestañas, 'datosMaestros.carpetas.ver'
            // solo a la pestaña Carpetas (ver el filtrado dentro de AdminDatosMaestrosPage).
            { to: '/admin/datos-maestros', icon: 'bi bi-database-fill-gear', label: 'Datos Maestros', permiso: ['datosMaestros.ver', 'datosMaestros.carpetas.ver'] },
            { to: '/admin/backups', icon: 'bi bi-cloud-arrow-up', label: 'Backups', permiso: 'administracion.ver' },
            { to: '/admin/email', icon: 'bi bi-envelope-at-fill', label: 'Correo Externo', permiso: 'correoExterno.ver' },
            // Importar planillas del estudio por lote (revisable antes de aplicar).
            { to: '/admin/importaciones', icon: 'bi bi-file-earmark-arrow-up', label: 'Importaciones', permiso: 'administracion.ver' }
        ]
    },
    {
        id: 'configuraciones',
        title: 'CONFIGURACIONES',
        icon: 'bi bi-gear-fill',
        // RECEPCION y SUPERVISOR entran solo por Configuración Turnero. "Mis notificaciones"
        // no tiene permiso propio a propósito: es una preferencia personal, la ve cualquier
        // usuario autenticado sin importar su rol (ver seccionVisible más abajo).
        permisos: ['administracion.ver', 'turnero.config'],
        items: [
            { to: '/configuraciones/notificaciones', icon: 'bi bi-bell', label: 'Mis notificaciones' },
            { to: '/admin/whatsapp', icon: 'bi bi-whatsapp', label: 'Configuración WhatsApp', permiso: 'administracion.ver' },
            { to: '/admin/anses-search', icon: 'bi bi-search', label: 'Configuración ANSES', permiso: 'administracion.ver' },
            // Horarios y cupos del turnero: ADMIN, PROGRAMADOR, SUPERVISOR y RECEPCION.
            { to: '/admin/turnero', icon: 'bi bi-clock', label: 'Configuración Turnero', permiso: 'turnero.config' }
        ]
    }
];

// Agrupación de alto nivel para el riel compacto: Clientes y Trámites quedan aparte
// (íconos fijos, sin flyout, son los destinos más usados). Los demás módulos se agrupan
// para reducir la carga cognitiva del menú.
export const GRUPOS_NAV = [
    // Turnos va primero y solo: son cinco pantallas que se usan todo el día y estaban
    // escondidas dentro de "General", mezcladas con Médicos, Gastos y Agenda.
    { id: 'turnos', title: 'Turnos', icon: 'bi bi-calendar-check-fill', sectionIds: ['recepcion'] },
    { id: 'listados', title: 'Listados', icon: 'bi bi-list-check', sectionIds: ['listados', 'listadosOtros'] },
    { id: 'general', title: 'General', icon: 'bi bi-grid-1x2-fill', sectionIds: ['general'] },
    { id: 'reportes', title: 'Reportes', icon: 'bi bi-bar-chart-fill', sectionIds: ['reportes'] },
    { id: 'art', title: 'ART', icon: 'bi bi-heart-pulse-fill', sectionIds: ['art'] },
    { id: 'rti', title: 'RTI', icon: 'bi bi-folder-fill', sectionIds: ['rti'] },
    { id: 'herramientas', title: 'Herramientas', icon: 'bi bi-tools', sectionIds: ['herramientas'] },
    { id: 'administracion', title: 'Administración', icon: 'bi bi-shield-lock-fill', sectionIds: ['administracion'] },
    { id: 'configuraciones', title: 'Configuraciones', icon: 'bi bi-gear-fill', sectionIds: ['configuraciones'] },
];

// Sección visible si tiene `permiso` y el usuario lo tiene, si tiene `permisos`
// (array) y el usuario tiene al menos uno de ellos, o si algún ítem de la sección no
// exige permiso propio (es una preferencia personal, visible para cualquier usuario
// autenticado — ej. "Mis notificaciones" dentro de CONFIGURACIONES).
export const seccionVisible = (section, puede) => {
    if (section.permiso) return puede(section.permiso);
    if ((section.items || []).some((item) => !item.permiso)) return true;
    return (section.permisos || []).some(puede);
};

// Secciones visibles para el usuario actual, con el caso especial de ART (pisa el `to`
// del ítem "Agenda" a /art/calendario) y el filtro de items por su propio permiso ya
// aplicados — mismo resultado que calculaba antes NavegacionLateral.js de forma local.
export const getSeccionesVisibles = (user, puede) => {
    const roleUsuario = user?.rol?.toUpperCase();
    return menuSections
        .filter((section) => seccionVisible(section, puede))
        .map((section) => {
            if (section.id === 'general' && roleUsuario === 'ART') {
                return {
                    ...section,
                    items: section.items.map((item) =>
                        item.key === 'agenda' ? { ...item, to: '/art/calendario' } : item
                    )
                };
            }
            return section;
        })
        .map((section) => ({
            // Filtro por item: algunos items restringen a un permiso más específico
            // que el de la sección (ej. feature en prueba, sub-permiso de configuración).
            // `item.permiso` también puede ser un array: alcanza con tener uno (mismo
            // criterio "OR" que `section.permisos` y que PermisoRoute).
            ...section,
            items: (section.items || []).filter((item) => {
                if (!item.permiso) return true;
                const permisos = Array.isArray(item.permiso) ? item.permiso : [item.permiso];
                return permisos.some(puede);
            }),
        }));
};

// Cada grupo del riel/drawer solo se muestra si al menos una de sus secciones miembro
// todavía tiene items visibles para el rol actual.
export const getGruposVisibles = (seccionesVisibles) => GRUPOS_NAV
    .map((grupo) => ({
        ...grupo,
        secciones: grupo.sectionIds
            .map((id) => seccionesVisibles.find((s) => s.id === id))
            .filter((s) => s && ((s.items?.length || 0) > 0 || (s.reportesItems?.length || 0) > 0)),
    }))
    .filter((grupo) => grupo.secciones.length > 0);

export const esRutaActiva = (pathname, ruta) => {
    if (!ruta) return false;
    return pathname === ruta || pathname.startsWith(`${ruta}/`);
};

export const seccionTieneRutaActiva = (pathname, section) => {
    if (!section) return false;
    const items = [...(section.items || []), ...(section.reportesItems || [])];
    return items.some((item) => esRutaActiva(pathname, item.to));
};
